import React, { useMemo, useRef } from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { BaseModal } from "@whatslly/ui-components/modals/BaseModal";
import { IModalInstance, ModalHeader } from "@whatslly/ui-components/modals";
import { ModalButtons } from "@whatslly/ui-components/ModalButtons";
import { FormElement } from "@whatslly/ui-components/v2/FormElements/Form";
import { FormSelect } from "@whatslly/ui-components/v2/FormElements/FormSelect";
import { Label } from "@whatslly/ui-components/v2/BaseElements/Label";
import { useDependencyInjector } from "@whatslly/commons/di";
import { UserGroupsStore } from "../../../UserGroups/UserGroupsStore";
import type { TemplateLanguages } from "@whatslly/types/waba/WhatsappBusinessApiTemplate";
import { FormikProps } from "formik";
import styles from "./EditTemplateModal.module.scss";

type FormValues = {
  groups: { label: string; value: string }[];
};

type EditTemplateModalProps = IModalInstance<{ groupIds: string[] }> & {
  template: TemplateLanguages;
  initialGroupIds: string[];
};

export const EditTemplateModal: React.FC<EditTemplateModalProps> = observer(({ closeModal, template, initialGroupIds }) => {
  const { t } = useTranslation();
  const userGroupsStore = useDependencyInjector(UserGroupsStore);
  const formikRef = useRef<FormikProps<FormValues>>(null);

  const groupOptions = useMemo(
    () => userGroupsStore.groupsList.case({ fulfilled: (groups) => groups.map((g) => ({ label: g.name, value: g._id })) }) ?? [],
    [userGroupsStore.groupsList]
  );

  const defaultGroupsValue = useMemo(
    () => groupOptions.filter((g) => initialGroupIds.includes(g.value)),
    [groupOptions, initialGroupIds]
  );

  const handleSubmit = (values: FormValues) => {
    closeModal({ groupIds: values.groups.map((g) => g.value) });
  };

  return (
    <BaseModal
      testId="edit-template-modal"
      header={
        <ModalHeader closeModal={() => closeModal()}>
          {t("whatsapp_business_api_settings_page.edit_template.title")}
        </ModalHeader>
      }
      closeModal={closeModal}
      closeOnClickOutside={false}
      closeOnEscape={false}
      className={styles.editModal}
      bodyClassName={styles.editModalBody}
    >
      <FormElement initialValues={{ groups: defaultGroupsValue }} onSubmit={handleSubmit} formikRef={formikRef}>
        <div className={styles.editModalContent}>
          <div className={styles.field}>
            <Label text={t("whatsapp_business_api_settings_page.edit_template.user_groups_label")} />
            <FormSelect<{ label: string; value: string }, true>
              name="groups"
              selectSize="small"
              paddingInline="none"
              options={groupOptions}
              defaultValue={defaultGroupsValue}
              isMulti={true}
              placeholder={t("whatsapp_business_api_settings_page.edit_template.all_users")}
              data-testid="edit-template-groups-select"
            />
          </div>
        </div>
        <ModalButtons
          buttonType="button"
          testId="edit-template-save-button"
          submitText={t("destinations_page.save")}
          onSubmit={() => formikRef.current?.submitForm()}
          onCancel={() => closeModal()}
        />
      </FormElement>
    </BaseModal>
  );
});
